/**
 * Phase 9.2: Personal Semantic Contexts Service
 * Manages user-specific interest vectors that personalize search results
 */

import { supabase } from '../lib/supabase';

class PersonalizationService {
  /**
   * Update user context with new interaction
   * @param {string} userId - User ID
   * @param {string} text - Text content to add to context (title, tags, query, etc.)
   * @param {Object} options - Additional options
   * @param {string} options.source - Source of the update (search, item, reflection)
   * @param {Array} options.tokens - Query tokens if available
   */
  async updateUserContext(userId, text, options = {}) {
    console.log('[CTX] updateUserContext called', { 
      userId, 
      text: text?.substring(0, 50), 
      source: options.source,
      hasTokens: Boolean(options.tokens),
      tokenCount: options.tokens?.length 
    });

    try {
      if (!text || !text.trim()) {
        console.log('[CTX] No text to process, skipping');
        return { ok: true, message: 'No text to process' };
      }

      // Build metadata FIRST before any async operations
      const metadata = {
        last_query: text.trim(),
        timestamp: new Date().toISOString(),
        source: options.source || 'unknown',
      };

      // Add token information if available
      if (options.tokens && Array.isArray(options.tokens)) {
        metadata.query_tokens = options.tokens.map(t => 
          typeof t === 'string' ? t : t.value || t.text || String(t)
        );
        metadata.token_count = options.tokens.length;
      } else {
        // Simple word count fallback
        metadata.query_tokens = text.trim().split(/\s+/).filter(Boolean);
        metadata.token_count = metadata.query_tokens.length;
      }

      console.log('[CTX] Metadata built:', JSON.stringify(metadata));

      // Generate embedding for the new text
      console.log('[CTX] Generating embedding...');
      const embeddingResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/deepsearch-auto-embed`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            text: text.trim(),
            item_id: crypto.randomUUID(), // Temporary ID for embedding generation
          }),
        }
      );

      if (!embeddingResponse.ok) {
        throw new Error('Failed to generate embedding');
      }

      const { embedding } = await embeddingResponse.json();
      
      if (!embedding || !Array.isArray(embedding)) {
        throw new Error('Invalid embedding format');
      }

      console.log('[CTX] Embedding generated, dimensions:', embedding.length);

      // Convert embedding array to vector format for RPC
      const embeddingVector = embedding; // Use array directly, not string

      console.log('[CTX] Calling update_user_context RPC with metadata:', metadata);

      // Update user context via RPC (blends with existing if present)
      const { data: rpcData, error: updateError } = await supabase.rpc('update_user_context', {
        p_user_id: userId,
        p_embedding: embeddingVector,
        p_metadata: metadata,
      });

      if (updateError) {
        console.error('[CTX] RPC error:', updateError);
        throw new Error(`Failed to update context: ${updateError.message}`);
      }

      console.log('[CTX] RPC success, response:', rpcData);
      return { ok: true, message: 'Context updated' };
    } catch (error) {
      console.error('[CTX] FAILED:', error);
      // Non-blocking - don't fail if personalization fails
      return { ok: false, error: error.message };
    }
  }

  /**
   * Get user's current context embedding
   * @param {string} userId - User ID
   * @returns {Promise<Array|null>} - User context embedding or null
   */
  async getUserContext(userId) {
    try {
      // Use RPC wrapper instead of direct table access
      const { data, error } = await supabase.rpc('get_user_context', {
        p_user_id: userId
      });

      if (error) {
        if (error.code === 'PGRST116') {
          // No context yet
          return null;
        }
        throw error;
      }

      // RPC returns array, get first item
      const userData = Array.isArray(data) && data.length > 0 ? data[0] : null;

      if (!userData) {
        return null;
      }

      // Convert vector to array if needed
      let embeddingArray = null;
      if (userData?.embedding) {
        if (Array.isArray(userData.embedding)) {
          embeddingArray = userData.embedding;
        } else if (typeof userData.embedding === 'string') {
          // Parse from string format "[1,2,3]"
          try {
            embeddingArray = JSON.parse(userData.embedding);
          } catch {
            // If parsing fails, try extracting from vector string
            embeddingArray = null;
          }
        }
      }

      return embeddingArray ? {
        embedding: embeddingArray,
        interaction_count: userData.interaction_count || 0,
        last_updated: userData.last_updated,
      } : null;
    } catch (error) {
      console.error('Failed to get user context:', error);
      return null;
    }
  }

  /**
   * Blend query embedding with user context
   * @param {Array} queryEmbedding - Query embedding vector
   * @param {Array} userContextEmbedding - User context embedding vector
   * @param {number} contextWeight - Weight of user context (0-1, default 0.3 = 30% context, 70% query)
   * @returns {Array} - Blended embedding
   */
  blendEmbeddings(queryEmbedding, userContextEmbedding, contextWeight = 0.3) {
    if (!userContextEmbedding || !Array.isArray(userContextEmbedding)) {
      return queryEmbedding;
    }

    if (!Array.isArray(queryEmbedding) || queryEmbedding.length !== userContextEmbedding.length) {
      console.warn('Embedding dimension mismatch, using query only');
      return queryEmbedding;
    }

    // Blend: (1 - contextWeight) * query + contextWeight * context
    // This gives query primary weight but adds personal flavor
    return queryEmbedding.map((q, i) => {
      const c = userContextEmbedding[i] || 0;
      return (1 - contextWeight) * q + contextWeight * c;
    });
  }

  /**
   * Update context from item interaction (click, view, save)
   * @param {Object} item - Item that was interacted with
   */
  async updateContextFromItem(item) {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        return;
      }

      // Build context text from item
      const contextText = [
        item.title || '',
        item.description || '',
        // Add auto_tags topics if available
        ...(item.auto_tags?.topics || []),
        ...(item.auto_tags?.objects || []),
        // Add tags if available
        ...(Array.isArray(item.tags) ? item.tags : []),
      ].filter(Boolean).join(' ');

      if (contextText.trim()) {
        await this.updateUserContext(session.session.user.id, contextText, {
          source: 'item',
          tokens: [item.title, ...(item.auto_tags?.topics || [])].filter(Boolean),
        });
      }
    } catch (error) {
      console.error('Failed to update context from item:', error);
      // Non-blocking
    }
  }

  /**
   * Update context from search query
   * @param {string|Array} query - Search query (string or token array)
   */
  async updateContextFromSearch(query) {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        return;
      }

      let queryText = '';
      let tokens = [];

      // Handle both string and token array inputs
      if (typeof query === 'string') {
        queryText = query.trim();
        tokens = queryText.split(/\s+/).filter(Boolean);
      } else if (Array.isArray(query)) {
        // Extract text from token objects
        tokens = query.map(t => typeof t === 'string' ? t : t.value || t.text || String(t));
        queryText = tokens.join(' ');
      }

      // Only update for meaningful searches (longer than 2 chars)
      if (queryText.length > 2) {
        await this.updateUserContext(session.session.user.id, queryText, {
          source: 'search',
          tokens: tokens,
        });
      }
    } catch (error) {
      console.error('Failed to update context from search:', error);
      // Non-blocking
    }
  }

  /**
   * Update context from reflection
   * @param {Object} reflection - Reflection data
   */
  async updateContextFromReflection(reflection) {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        return;
      }

      const reflectionText = [
        reflection.text || '',
        reflection.title || '',
        reflection.mood || '',
        reflection.tags?.join(' ') || '',
      ].filter(Boolean).join(' ');

      if (reflectionText.trim()) {
        await this.updateUserContext(session.session.user.id, reflectionText.trim(), {
          source: 'reflection',
          tokens: [
            reflection.title,
            reflection.mood,
            ...(reflection.tags || []),
          ].filter(Boolean),
        });
      }
    } catch (error) {
      console.error('Failed to update context from reflection:', error);
      // Non-blocking
    }
  }

  /**
   * Get user context status
   * @param {string} userId - User ID
   */
  async getContextStatus(userId) {
    try {
      const { data, error } = await supabase
        .schema('deepsearch').from('user_context_status')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to get context status:', error);
      return null;
    }
  }
}

export default new PersonalizationService();

